/**
 * Prompt handoff — one-shot context transfer between Triveny pages.
 *
 * Used when one surface (e.g. TrivenyHome's landing-page composer modal)
 * hands a user-typed prompt off to a downstream surface (e.g. WebPresencePage's
 * AI Studio tab). The downstream page reads the pending prompt on mount and
 * clears it — so the prompt is consumed exactly once and doesn't leak across
 * sessions.
 *
 * sessionStorage (not localStorage) so the handoff survives a tab navigation
 * but clears on browser close. Scoped by `surface` string so multiple handoffs
 * don't collide.
 */

const STORAGE_PREFIX = 'triveny:pending-prompt:';

export interface PendingPrompt {
  /** Free-text prompt the user typed in the upstream surface */
  prompt: string;
  /** Optional sub-surface hint (e.g. 'landing' vs 'email' inside Studio) */
  intent?: string;
  /** Optional extra context (template id, brand tokens, etc.) */
  meta?: Record<string, unknown>;
}

/** Store a pending prompt for `surface`. Overwrites any prior pending value. */
export function setPendingPrompt(surface: string, payload: PendingPrompt): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + surface, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable in incognito / SSR contexts — ignore.
  }
}

/**
 * Read and remove the pending prompt for `surface`. Returns null if none.
 * Intended to be called from a useEffect on the downstream page.
 */
export function consumePendingPrompt(surface: string): PendingPrompt | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + surface);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_PREFIX + surface);
    return JSON.parse(raw) as PendingPrompt;
  } catch {
    return null;
  }
}

/** Surface name constants — prevents typos at the two call sites */
export const PROMPT_SURFACES = {
  studio: 'studio',
} as const;
